/**
 * Global keyboard-shortcut registry.
 *
 * Lets any feature on the platform register its own shortcut without touching
 * a single global keydown listener. The single listener lives in
 * <GlobalShortcutsProvider> (see ../hooks/useGlobalShortcut.jsx) which dispatches
 * matched events to the registered handler.
 *
 * Key combination format (string or array of strings):
 *   "mod+s"           → Cmd on Mac, Ctrl on Windows/Linux
 *   "mod+shift+z"     → Cmd/Ctrl + Shift + Z
 *   "?"               → literal `?` (no modifiers)
 *   "mod+y"           → Cmd/Ctrl + Y (often a synonym for redo)
 *
 * Modifier order doesn't matter: "mod+shift+z" === "shift+mod+z".
 * Keys are case-insensitive.
 *
 * Each registered shortcut requires a unique `id` (used for unregister + dedupe).
 * `category` groups shortcuts in the help dialog (e.g. "Editing", "Navigation").
 */

const _registry = new Map(); // id → ShortcutDefinition
const _listeners = new Set();
let _snapshot = []; // cached snapshot — re-built only on register/unregister

function _notify() {
  _snapshot = Array.from(_registry.values());
  for (const fn of _listeners) {
    try { fn(); } catch (e) { console.error('shortcut listener failed:', e); }
  }
}

/**
 * @param {object} def
 * @param {string} def.id           Unique identifier (e.g. "report-builder.save")
 * @param {string|string[]} def.keys  Single combo or list of equivalent combos
 * @param {string} def.label        Human-readable description (shown in help dialog)
 * @param {string} [def.category]   Group label for the help dialog (default "General")
 * @param {Function} def.handler    (event) => void — called when matched
 * @param {boolean} [def.preventDefault=true]  Whether to call event.preventDefault()
 * @param {boolean} [def.allowInInputs=false]  When false, the shortcut does NOT fire
 *                                             while focus is inside an <input>, <textarea>,
 *                                             or contentEditable element. Set true for global
 *                                             shortcuts like "?" / "mod+k" that should always work.
 */
export function registerShortcut(def) {
  if (!def || !def.id || !def.keys || !def.handler) {
    throw new Error('registerShortcut: id, keys, and handler are required');
  }
  const keys = Array.isArray(def.keys) ? def.keys : [def.keys];
  _registry.set(def.id, {
    ...def,
    keys: keys.map(_normalizeCombo),
    category: def.category || 'General',
    preventDefault: def.preventDefault !== false,
    allowInInputs: !!def.allowInInputs,
  });
  _notify();
}

export function unregisterShortcut(id) {
  if (_registry.delete(id)) _notify();
}

export function getRegisteredShortcuts() {
  return _snapshot;
}

export function subscribeToShortcuts(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/** Normalises "Mod+Shift+Z" → { mod:true, shift:true, alt:false, key:"z" }. */
export function _normalizeCombo(combo) {
  const parts = combo.toLowerCase().split('+').map(s => s.trim());
  const mods = { mod: false, shift: false, alt: false };
  let key = '';
  for (const p of parts) {
    if (p === 'mod' || p === 'cmd' || p === 'ctrl') mods.mod = true;
    else if (p === 'shift') mods.shift = true;
    else if (p === 'alt' || p === 'option') mods.alt = true;
    else key = p;
  }
  return { ...mods, key };
}

/**
 * Characters that require Shift on a US keyboard to type. When a combo's key is
 * one of these (e.g. "?" or "!"), we don't enforce the shift modifier on match
 * — what the user typed naturally already includes shift.
 */
const SHIFTED_CHARS = new Set(['?', '!', '@', '#', '$', '%', '^', '&', '*', '(', ')', '_', '+', '{', '}', '|', ':', '"', '<', '>', '~']);

/** Returns true if a keyboard event matches one of the combos. */
export function matchesCombo(event, combos) {
  const mod = event.metaKey || event.ctrlKey;
  const evKey = (event.key || '').toLowerCase();
  for (const c of combos) {
    if (c.mod !== mod) continue;
    if (c.alt !== event.altKey) continue;
    // Shift is required to match unless the combo's key is a shifted-printable like "?"
    if (!SHIFTED_CHARS.has(c.key) && c.shift !== event.shiftKey) continue;
    if (c.key !== evKey) continue;
    return true;
  }
  return false;
}

/** Pretty-prints a combo for the help dialog (e.g. "⌘+S" on mac, "Ctrl+S" elsewhere). */
export function formatCombo(combo) {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const parts = [];
  if (combo.mod) parts.push(isMac ? '⌘' : 'Ctrl');
  if (combo.alt) parts.push(isMac ? '⌥' : 'Alt');
  if (combo.shift) parts.push(isMac ? '⇧' : 'Shift');
  if (combo.key === ' ') parts.push('Space');
  else if (combo.key.length === 1) parts.push(combo.key.toUpperCase());
  else parts.push(combo.key.charAt(0).toUpperCase() + combo.key.slice(1));
  return parts.join(isMac ? '' : '+');
}

/** Test-only helper to clear the registry between tests. */
export function _resetForTest() {
  _registry.clear();
  _listeners.clear();
  _snapshot = [];
}
